import Material from '../materials/Material.js';
import Impostor from '../rendering/Impostor.js';
import Debug from '../utils/Debug.js';

/**
 * Synchronizes world state between peers.
 * Hooks into World and MaterialGroupManager mutation callbacks.
 * Applies incoming remote operations to the local world.
 */
export default class WorldSync {
  /**
   * @param {World} world
   * @param {NetworkManager} networkManager
   */
  constructor(world, networkManager) {
    this.world = world;
    this.network = networkManager;
    this._isApplyingRemote = false;
    // Set to true when this peer is established and can serve world state.
    // Prevents newcomers from responding to sync requests with empty/partial worlds.
    // Set by main.js when: (1) we're the first peer (alone timer), or
    // (2) we've received and applied world state from another peer.
    this._isReady = false;
    // When true, all mutation broadcasts and remote op applications are suppressed.
    // Used during dungeon mode: dungeon geometry is deterministic (seeded) and
    // never synced; main world ops from non-dungeon peers are irrelevant.
    this._isSuppressed = false;
    // Pre-serialized main world JSON to serve while suppressed (in dungeon).
    // Set by DungeonInstanceManager before entering dungeon so new joiners
    // still receive the main world state instead of getting no response.
    this._savedWorldStateJson = null;

    // Hook world mutations — fires on every add/remove/update
    this._originalOnMutation = world.onMutation;
    world.onMutation = (op) => this._onLocalMutation(op);

    // Hook material group mutations
    const mgm = world.materialGroupManager;
    if (mgm) {
      this._originalGroupOnMutation = mgm.onMutation;
      mgm.onMutation = (op) => this._onLocalMutation(op);
    }

    // Hook NPC mutations
    const npcMgr = world.npcManager;
    if (npcMgr) {
      this._originalNpcOnMutation = npcMgr.onMutation;
      npcMgr.onMutation = (op) => this._onLocalMutation(op);
    }

    // Listen for remote operations
    this.network.onOperation = (data, peerId) => this._applyRemoteOp(data);
    this.network.onSyncRequest = (peerId) => this._handleSyncRequest(peerId);
    this.network.onSyncRequestRaw = (peerId) => this._handleSyncRequestRaw(peerId);
    this.network.onWorldState = (json) => this._loadWorldState(json);
  }

  /**
   * Called when the local world or material group is mutated.
   * Broadcasts to all peers.
   * Skipped when applying remote ops (prevents re-broadcast loops).
   */
  _onLocalMutation(op) {
    if (this._isApplyingRemote) return;
    if (this._isSuppressed) return;
    if (!this.network.isConnected) return;

    op.ts = Date.now();
    op.peerId = this.network.peerId;
    this.network.broadcastOperation(op);
  }

  /**
   * Apply a remote operation to the local world.
   */
  _applyRemoteOp(op) {
    if (this._isSuppressed) return;
    this._isApplyingRemote = true;
    try {
      switch (op.op) {
        case 'addCuboid': {
          const d = op.data;
          const materials = this._deserializeMaterials(d.materials);
          this.world.addCuboid(d.x, d.y, d.z, d.width, d.height, d.depth, materials);
          break;
        }
        case 'removeCuboid': {
          const d = op.data;
          this.world.removeCuboid(d.x, d.y, d.z, d.width, d.height, d.depth);
          break;
        }
        case 'updateMaterials': {
          const d = op.data;
          const materials = this._deserializeMaterials(d.materials);
          this.world.updateCuboidMaterials(d.x, d.y, d.z, d.width, d.height, d.depth, materials);
          break;
        }
        case 'addImpostor': {
          const impostor = new Impostor(op.data);
          impostor._isVegetation = true;
          this.world.addImpostor(impostor);
          impostor.sampleAO();
          break;
        }
        case 'removeImpostor': {
          this.world.removeImpostor(op.data.id);
          break;
        }
        case 'clear': {
          this.world.clear();
          // Also clear NPCs — world.clear() doesn't touch npcManager
          const npcMgr = this.world.npcManager;
          if (npcMgr) {
            npcMgr._suppressMutation = true;
            npcMgr.removeAll();
            npcMgr._suppressMutation = false;
          }
          break;
        }
        // --- Material Group Operations ---
        case 'syncGroup': {
          this._applySyncGroup(op.data);
          break;
        }
        case 'deleteGroup': {
          const mgm = this.world.materialGroupManager;
          if (mgm) mgm.deleteGroup(op.data.groupId);
          break;
        }
        case 'removeGroupFaces': {
          const mgm = this.world.materialGroupManager;
          if (mgm) mgm.removeFacesFromGroup(op.data.groupId, op.data.faces);
          break;
        }
        // --- NPC Operations ---
        case 'spawnNPC': {
          const npcMgr = this.world.npcManager;
          if (npcMgr) {
            // Suppress onMutation — spawnNPC is async, _isApplyingRemote resets before it completes
            npcMgr._suppressMutation = true;
            npcMgr.spawnNPC(op.data)
              .catch(e => Debug.error('WorldSync', 'Remote NPC spawn failed:', e))
              .finally(() => { npcMgr._suppressMutation = false; });
          }
          break;
        }
        case 'removeNPC': {
          const npcMgr = this.world.npcManager;
          if (npcMgr) {
            npcMgr._suppressMutation = true;
            npcMgr.removeNPC(op.data.id);
            npcMgr._suppressMutation = false;
          }
          break;
        }
      }
    } finally {
      this._isApplyingRemote = false;
    }
  }

  /**
   * Apply a full group state sync — creates or updates a group.
   * @param {Object} groupData - Serialized group (same format as World.toJSON groups)
   */
  _applySyncGroup(groupData) {
    if (!groupData) return;
    const mgm = this.world.materialGroupManager;
    if (!mgm) return;

    const groupId = groupData.id;

    // Delete existing group if it exists (clean slate for update)
    if (mgm.groups.has(groupId)) {
      mgm.deleteGroup(groupId);
    }

    // Create the group with full settings
    const group = mgm.createGroup({
      id: groupData.id,
      name: groupData.name || undefined,
      material: groupData.material ? Material.fromJSON(groupData.material) : null,
      textureScale: groupData.textureScale,
      textureScaleLinked: groupData.textureScaleLinked,
      textureOffset: groupData.textureOffset,
      textureWidth: groupData.textureWidth,
      textureHeight: groupData.textureHeight,
      videoScale: groupData.videoScale,
      videoScaleLinked: groupData.videoScaleLinked,
      videoOffset: groupData.videoOffset,
      gradientScale: groupData.gradientScale,
      gradientScaleLinked: groupData.gradientScaleLinked,
      gradientAngle: groupData.gradientAngle,
      gradientAxis: groupData.gradientAxis,
      gradientOffset: groupData.gradientOffset,
      patternScale: groupData.patternScale,
      patternScaleLinked: groupData.patternScaleLinked,
      patternOffset: groupData.patternOffset,
      patternRotation: groupData.patternRotation,
      colorOpacity: groupData.colorOpacity,
      effectScale: groupData.effectScale,
      effectScaleLinked: groupData.effectScaleLinked,
      effectOffset: groupData.effectOffset,
      effectRotation: groupData.effectRotation,
      textScale: groupData.textScale,
      textScaleLinked: groupData.textScaleLinked,
      textOffset: groupData.textOffset,
      textAxis: groupData.textAxis
    });

    // Assign faces
    if (groupData.faces && Array.isArray(groupData.faces)) {
      mgm.assignFacesToGroup(group.id, groupData.faces);
    }

    // Apply material with triplanar projection
    mgm.applyGroupMaterial(group.id);
  }

  /**
   * Handle a peer requesting the full world state.
   * Only responds if we're established (_isReady) — prevents newcomers from
   * responding with empty/partial worlds that overwrite the requester's state.
   * Deferred so toJSON() doesn't block the WebRTC event handler.
   */
  _handleSyncRequest(peerId) {
    if (!this._isReady) return;

    // In dungeon: serve saved main world state so new joiners aren't stuck
    if (this._isSuppressed) {
      if (!this._savedWorldStateJson) return;
      setTimeout(() => {
        if (!this.network.isConnected) return;
        this.network.sendWorldState(peerId, this._savedWorldStateJson);
      }, 0);
      return;
    }

    setTimeout(() => {
      if (!this.network.isConnected) return;
      // Pre-serialize so sendWorldState skips redundant JSON.stringify()
      const jsonStr = JSON.stringify(this.world.toJSON());
      this.network.sendWorldState(peerId, jsonStr);
    }, 0);
  }

  /**
   * Handle a peer requesting uncompressed world state (decompression fallback).
   * Same as _handleSyncRequest but forces uncompressed transfer.
   * @param {string} peerId
   */
  _handleSyncRequestRaw(peerId) {
    if (!this._isReady) return;

    Debug.log('WorldSync', 'Peer requested uncompressed world state (decompression fallback)');

    // In dungeon: serve saved main world state so new joiners aren't stuck
    if (this._isSuppressed) {
      if (!this._savedWorldStateJson) return;
      setTimeout(() => {
        if (!this.network.isConnected) return;
        this.network.sendWorldState(peerId, this._savedWorldStateJson, { compress: false });
      }, 0);
      return;
    }

    setTimeout(() => {
      if (!this.network.isConnected) return;
      // Pre-serialize so sendWorldState skips redundant JSON.stringify()
      const jsonStr = JSON.stringify(this.world.toJSON());
      this.network.sendWorldState(peerId, jsonStr, { compress: false });
    }, 0);
  }

  /**
   * Load full world state received from another peer.
   * Deferred so fromJSON() (expensive — rebuilds DOM, materials, AO)
   * doesn't block the WebRTC receive handler.
   */
  _loadWorldState(json) {
    const diag = this.network._onDiagnostic;
    diag?.(`fromJSON: ${json.cuboids?.length || 0} cuboids, ${json.npcs?.length || 0} NPCs`);
    setTimeout(() => {
      Debug.log('WorldSync', `Applying remote world state: ${json.cuboids?.length || 0} cuboids, ${json.npcs?.length || 0} NPCs, ${json.materialGroups?.length || 0} groups`);
      diag?.('Running fromJSON...');
      this._isApplyingRemote = true;
      try {
        this.world.fromJSON(json, true, { batchCuboids: true });
        // Re-apply all group materials after world load to ensure triplanar
        // settings survive across platforms. fromJSON Phase 3 already applies
        // group materials after update(), but this safety net catches any edge
        // cases where face dimensions change after the initial pass.
        this._reapplyGroupMaterials();
      } catch (e) {
        Debug.error('WorldSync', 'fromJSON FAILED:', e);
        diag?.(`fromJSON FAILED: ${e.message}`);
      } finally {
        this._isApplyingRemote = false;
      }
      const cuboidCount = this.world.cuboids.size;
      diag?.(`fromJSON done: ${cuboidCount} cuboids in world`);
      Debug.log('WorldSync', `fromJSON complete: ${cuboidCount} cuboids now in world`);
      // Force an immediate re-render on the next game loop frame.
      // fromJSON's internal update() ran inside this setTimeout (outside the game loop),
      // so the camera may not have been fully updated by camera.update(). On Android
      // where zoom is baked into cuboid dimensions, this can cause cuboids to render
      // with stale camera state and appear invisible.
      // fragmentsNeedClearing triggers needsImmediateSort (bypasses rate limiting)
      // so the sort runs on the very next frame with proper camera context.
      this.world.fragmentsNeedClearing = true;
      this.world.forceRender();
    }, 0);
  }

  /**
   * Re-apply all material group triplanar settings.
   * Called after fromJSON to ensure group materials render correctly.
   */
  _reapplyGroupMaterials() {
    const mgm = this.world.materialGroupManager;
    if (!mgm || mgm.groups.size === 0) return;
    for (const groupId of mgm.groups.keys()) {
      mgm.applyGroupMaterial(groupId, true);
    }
  }

  /**
   * Deserialize material JSON back into Material instances.
   */
  _deserializeMaterials(materialsJson) {
    if (!materialsJson) return null;
    const result = {};
    for (const [face, matData] of Object.entries(materialsJson)) {
      result[face] = Material.fromJSON(matData);
    }
    return result;
  }

  /**
   * Reset sync state for room transition (e.g., entering/leaving dungeon).
   * Clears _isReady flag without destroying mutation hooks.
   */
  reset() {
    this._isReady = false;
    this._isSuppressed = false;
    this._savedWorldStateJson = null;
  }

  /**
   * Clean up — restore original callbacks.
   */
  destroy() {
    this.world.onMutation = this._originalOnMutation;
    const mgm = this.world.materialGroupManager;
    if (mgm) {
      mgm.onMutation = this._originalGroupOnMutation;
    }
    const npcMgr = this.world.npcManager;
    if (npcMgr) {
      npcMgr.onMutation = this._originalNpcOnMutation;
    }
    this.network.onOperation = null;
    this.network.onSyncRequest = null;
    this.network.onSyncRequestRaw = null;
    this.network.onWorldState = null;
  }
}
